import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import LoadingIndicator from '../common/LoadingIndicator';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { formatCustomTagsToLaTeX } from '../common/QuestionFormatter';
import { getExistingCertificate } from '../../utils/certificateCleanup';

// MathJax configuration (can reuse the same one)
const mathJaxConfig = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    inlineMath: [["$", "$"], ["\\(", "\\)"]], // $...$ and \(...\)
    displayMath: [["$$", "$$"], ["\\[", "\\]"]], // $$...$$ and \[...\]
    processEscapes: true,
    macros: { // Add support for \ce{} macro
      '\\ce': '{\\text{#1}}'
    }
  },
  options: { // Optional: Configure MathJax options if needed
    enableMenu: false, // Disable right-click MathJax menu
  },
};

function ResultsPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const [highestScore, setHighestScore] = useState(0);
  const [courseProgress, setCourseProgress] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState([]);

  // Get module and subcourse from URL parameters
  const searchParams = new URLSearchParams(window.location.search);
  const moduleId = searchParams.get('module');
  const subCourseId = searchParams.get('subcourse');

  useEffect(() => {
    const fetchResults = async () => {
      try {
        // Get current quiz results
        const resultId = `${courseId}_${moduleId}_${subCourseId}`;
        const resultDoc = await getDoc(doc(db, 'users', user.uid, 'examResults', resultId));
        
        if (resultDoc.exists()) {
          const resultData = resultDoc.data();
          console.log('Loaded exam results from Firestore:', resultData);
          console.log('Results path:', `users/${user.uid}/examResults/${resultId}`);
          setResults(resultData);
          setAttempts(resultData.attempts || 1);
          setHighestScore(resultData.highestScore || resultData.score);
        } else {
          console.warn('No results document found at path:', `users/${user.uid}/examResults/${resultId}`);
        }

        // Fetch quiz questions from course data
        const courseRef = doc(db, 'courses', courseId);
        const courseDoc = await getDoc(courseRef);
        if (courseDoc.exists()) {
          const courseData = courseDoc.data();
          const module = courseData.modules?.find(m => m.moduleId === moduleId);
          const subCourse = module?.subCourses?.find(sc => sc.subCourseId === subCourseId);
          if (subCourse?.quizData?.questions) {
            // Ensure each question has a correctAnswer field
            const questions = subCourse.quizData.questions.map(q => ({
              ...q,
              correctAnswer: Number(q.correctAnswer) // Ensure correctAnswer is a number
            }));
            setQuizQuestions(questions);
            console.log('Loaded quiz questions:', questions);
          }
        }

        // Get course progress
        const progressDoc = await getDoc(doc(db, 'users', user.uid, 'courseProgress', courseId));
        if (progressDoc.exists()) {
          setCourseProgress(progressDoc.data().progress);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching results:', error);
        setLoading(false);
      }
    };

    if (user) fetchResults();
  }, [user, courseId]);

  const handleMarkComplete = async () => {
    if (results.score < 75) {
      alert('You need to score at least 75% to mark this section as complete.');
      return;
    }

    try {
      setLoading(true);
      
      // Initialize batch at the beginning
      const batch = writeBatch(db);
      
      // Update user's course progress
      const userProgressRef = doc(db, 'users', user.uid, 'courseProgress', courseId);
      const userCompletedRef = doc(db, 'users', user.uid, 'completedSubCourses', courseId);
      
      // Get current course data
      const courseRef = doc(db, 'courses', courseId);
      const courseDoc = await getDoc(courseRef);
      const courseData = courseDoc.data();

      // Get current completion status
      const completedDoc = await getDoc(userCompletedRef);
      const completedData = completedDoc.exists() ? completedDoc.data() : { completed: {} };

      // Normalize existing completion keys to avoid duplicates (legacy keys included courseId prefix)
      const normalizedCompleted = {};
      const legacyPrefix = `${courseId}_`;
      Object.entries(completedData.completed || {}).forEach(([key, value]) => {
        let normalizedKey = key;
        if (normalizedKey.startsWith(legacyPrefix)) {
          normalizedKey = normalizedKey.slice(legacyPrefix.length); // strip courseId_
        }
        // Preserve any existing metadata; convert boolean to object placeholder
        normalizedCompleted[normalizedKey] =
          typeof value === 'object' && value !== null ? value : { completed: true };
      });

      // Mark this subcourse as completed
      const completionKey = `${moduleId}_${subCourseId}`;
      normalizedCompleted[completionKey] = {
        completedAt: new Date().toISOString(),
        score: results.score,
        attempts: attempts
      };

      // Calculate new progress
      const modulesArray = Array.isArray(courseData.modules) ? courseData.modules : [];
      const totalSubCourses = modulesArray.reduce(
        (total, module) => total + (Array.isArray(module.subCourses) ? module.subCourses.length : 0),
        0
      );
      const completedCount = Object.keys(normalizedCompleted).length;
      const newProgress = Math.min(100, totalSubCourses > 0 ? (completedCount / totalSubCourses) * 100 : 0);

      // Generate certificate if course is fully completed
      if (newProgress >= 100) {
        // Check for existing certificate and clean up duplicates
        const existingCert = await getExistingCertificate(user.uid, courseId);
        
        if (!existingCert) {
          // Only create certificate if one doesn't exist
          const verificationId = `IOF-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
          
          // Create certificate data
          const certificateData = {
            userId: user.uid,
            userName: user.displayName || user.email,
            courseId: courseId,
            courseName: courseData.title,
            completedAt: new Date().toISOString(),
            generatedAt: new Date().toISOString(),
            verificationId: verificationId,
          };
          if (courseData.category) {
            certificateData.category = courseData.category;
          }

          // Store certificate in certificates collection
          const certificateRef = doc(db, 'certificates', verificationId);
          batch.set(certificateRef, certificateData);
          
          // Update completed data with verification ID
          batch.set(userCompletedRef, {
            verificationId: verificationId,
            firstCompletedAt: new Date().toISOString()
          }, { merge: true });
        } else {
          // Use existing certificate verification ID
          const existingVerificationId = existingCert.data().verificationId;
          
          // Update completed data with existing verification ID
          batch.set(userCompletedRef, {
            verificationId: existingVerificationId,
            firstCompletedAt: new Date().toISOString()
          }, { merge: true });
        }
      }
      
      // Update completed subcourses
      batch.set(userCompletedRef, { completed: normalizedCompleted }, { merge: true });
      
      // Update progress
      batch.set(userProgressRef, {
        progress: newProgress,
        lastUpdated: new Date().toISOString()
      }, { merge: true });

      // Store the exam result
      const resultRef = doc(db, 'users', user.uid, 'examResults', `${courseId}_${moduleId}_${subCourseId}`);
      batch.set(resultRef, {
        ...results,
        attempts: attempts,
        highestScore: Math.max(results.score, highestScore),
        completedAt: new Date().toISOString(),
        courseId,
        moduleId,
        subCourseId
      });

      await batch.commit();
      
      // Navigate to certificate page if course is completed
      if (newProgress === 100) {
        navigate(`/courses/${courseId}/certificate`);
      } else {
        navigate(`/courses/${courseId}`);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      alert('Failed to update progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!results || !moduleId || !subCourseId) {
        console.error('Missing required data for retry');
        return;
    }

    // Only allow retry if score is below 75% and attempts are less than 3
    if (results.score >= 75) {
        alert('You have already passed this evaluation.');
        return;
    }

    if (attempts >= 3) {
        alert('You have reached the maximum number of attempts for this quiz.');
        return;
    }

    // If conditions are met, navigate to exam page with required parameters
    navigate(`/courses/${courseId}/exam?module=${moduleId}&subcourse=${subCourseId}`);
  };

  if (loading) return <LoadingIndicator />;

  // Graceful fallback if no results are available
  if (!results) {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-center">
          <h2 className="text-2xl font-bold mb-2">No Results Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find your evaluation results for this module. If you just submitted, please refresh in a moment, or return to the course page.</p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-[rgb(130,88,18)] text-white px-6 py-2 rounded hover:bg-[rgb(110,68,0)]"
              onClick={() => navigate(`/courses/${courseId}`)}
            >
              Back to Course
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-center mb-8">Evaluation Results</h1>
          
          {attempts >= 3 && results.score < 75 ? (
              <div className="text-center mb-8">
                  <h2 className="text-xl font-bold text-red-600 mb-4">
                      Course Failed
                  </h2>
                  <p className="text-gray-700">
                      You have exhausted all 3 attempts and did not achieve a passing score.
                      Please contact the administrator for further assistance.
                  </p>
              </div>
          ) : (
              <div className="mb-8">
                  <div className="text-center">
                      <h2 className="text-6xl font-bold text-gray-700 mb-4">
                          {results.score.toFixed(1)}%
                      </h2>
                      <p className="text-xl mb-2">
                          Grade: {results.score >= 75 ? 'Pass' : 'Fail'}
                      </p>
                      <p className="text-gray-600">
                          Attempts: {attempts}/3 {attempts < 3 && results.score < 75 ? `(Remaining: ${3 - attempts})` : ''}
                      </p>
                      <p className="text-gray-600">
                          Highest Score: {highestScore.toFixed(1)}%
                      </p>
                  </div>
              </div>
          )}

          <div className="space-y-4">
            <p className="text-center text-lg">
              Correct Answers: {results.correctAnswers} out of {results.totalQuestions}
            </p>
            {typeof results.timeTaken === 'number' && (
              <p className="text-center text-sm text-gray-600">Time Taken: {Math.max(results.timeTaken, 0)} seconds</p>
            )}

            {/* Show missed and correct questions */}
            {quizQuestions.length > 0 && results?.answers && (
              <div className="mt-8">
                <h3 className="text-xl font-bold mb-4 text-center">Your Answers</h3>
                <ul className="space-y-6">
                  {quizQuestions.map((q, idx) => {
                    const userAnswerIdx = results.answers?.[idx];
                    const isCorrect = Number(userAnswerIdx) === Number(q.correctAnswer);
                    return (
                      <li key={idx} className={`p-4 rounded-lg shadow border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}> 
                        <div className="font-semibold mb-2">
                          Q{idx + 1}: <MathJax>{formatCustomTagsToLaTeX(q.question)}</MathJax>
                        </div>
                        <div className="ml-4">
                          <span className="font-medium">Your Answer:</span>{' '}
                          {typeof userAnswerIdx === 'number' ? (
                            <MathJax>{formatCustomTagsToLaTeX(q.options[userAnswerIdx])}</MathJax>
                          ) : (
                            <span className="text-gray-600">No answer selected</span>
                          )}
                          <span className={`ml-4 px-2 py-1 rounded text-xs ${isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                            {isCorrect ? 'Correct' : 'Missed'}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            
            <div className="flex justify-center space-x-4 mt-8">
              {results.score >= 75 ? (
                <button
                  onClick={handleMarkComplete}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                >
                  Mark Complete
                </button>
              ) : (
                <button
                  onClick={handleRetry}
                  disabled={attempts >= 3}
                  className={`bg-[rgb(130,88,18)] text-white px-6 py-2 rounded-lg 
                    ${attempts >= 3 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[rgb(110,68,0)]'}`}
                >
                  Retry Evaluation
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
}

export default ResultsPage; 